/**
 * api/client.js
 *
 * Central Axios instance for all API requests.
 *
 * - Base URL comes from VITE_API_URL (.env), defaults to /api.
 *   In development the Vite proxy forwards /api → localhost:8000/api,
 *   so no CORS headers are needed during local dev.
 * - Attaches the Sanctum Bearer token to every outgoing request.
 * - Handles 401 globally: clears the stored token and reloads so the
 *   user lands on the login screen.
 */

import axios from 'axios';

const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type':     'application/json',
    'Accept':           'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,   // needed if you ever switch to cookie / SPA auth
});

// ── Request interceptor: attach Bearer token ─────────────────────────────
client.interceptors.request.use(config => {
  const token = localStorage.getItem('tayst_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: handle 401 globally ────────────────────────────
client.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Token expired or invalid — clear storage and bounce to login
      localStorage.removeItem('tayst_token');
      localStorage.removeItem('tayst_user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export default client;
